 
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useState, useEffect, useRef } from 'react';
import { Users, Circle, Edit2 } from 'lucide-react';
import { supabase } from '../../providers/AuthProvider';
import { RealtimeChannel } from '@supabase/supabase-js';
import { CustomStatusModal } from './CustomStatusModal';
import { useCustomStatus } from '../../hooks/useCustomStatus';

interface OnlineAdmin {
  userId: string;
  email?: string;
  status: 'online' | 'away' | 'busy';
  lastSeen: string;
}

export function OnlineAdmins() {
  const [onlineAdmins, setOnlineAdmins] = useState<OnlineAdmin[]>([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const { status: customStatus, updateStatus } = useCustomStatus();

  useEffect(() => {
    const channel = supabase.channel('admin_presence', {
      config: {
        presence: {
          key: 'admin',
        },
      },
    });
    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const admins: OnlineAdmin[] = [];
        
        Object.entries(state).forEach(([userId, presences]) => {
          const presence = Array.isArray(presences) ? presences[0] : presences;
          if (presence && presence.isAdmin) {
            admins.push({
              userId,
              email: presence.email,
              status: presence.status || 'online',
              lastSeen: presence.lastSeen || new Date().toISOString(),
            });
          }
        });
        
        setOnlineAdmins(admins);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        const presence = Array.isArray(newPresences) ? newPresences[0] : newPresences;
        if (presence && presence.isAdmin) {
          setOnlineAdmins((prev) => {
            if (prev.some(a => a.userId === key)) return prev;
            return [...prev, {
              userId: key,
              email: presence.email,
              status: presence.status || 'online',
              lastSeen: presence.lastSeen || new Date().toISOString(),
            }];
          });
        }
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        setOnlineAdmins((prev) => prev.filter(a => a.userId !== key));
      })
      .subscribe(async (subscribeStatus) => {
        if (subscribeStatus === 'SUBSCRIBED') {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await channel.track({
              userId: user.id,
              email: user.email,
              isAdmin: true,
              status: 'online',
              lastSeen: new Date().toISOString(),
            });
          }
        }
      });

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, []);

  return (
    <>
      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
        <Users className="w-4 h-4 text-emerald-400" />
        {onlineAdmins.length > 0 ? (
          <>
            <span className="text-xs text-slate-400">Admins online:</span>
            <div className="flex items-center gap-2">
              {onlineAdmins.map((admin) => (
                <div key={admin.userId} className="flex items-center gap-1">
                  <Circle className={`w-2 h-2 ${
                    admin.status === 'online' ? 'text-emerald-400 fill-emerald-400' :
                    admin.status === 'away' ? 'text-amber-400 fill-amber-400' :
                    'text-red-400 fill-red-400'
                  }`} />
                  <span className="text-xs text-slate-300">{admin.email?.split('@')[0] || 'Admin'}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <span className="text-xs text-slate-400">Nenhum admin online</span>
        )}
        {/* Custom status */}
        {customStatus && (
          <div className="flex items-center gap-1 ml-2 pl-2 border-l border-white/10">
            <span className="text-xs">{customStatus.emoji || '💼'}</span>
            <span className="text-xs text-slate-300">{customStatus.message}</span>
          </div>
        )}
        <button
          onClick={() => setShowStatusModal(true)}
          className="ml-2 p-1 hover:bg-white/5 rounded transition-colors"
          title="Editar status"
        >
          <Edit2 className="w-3 h-3 text-slate-400" />
        </button>
      </div>
      <CustomStatusModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={customStatus ? { message: customStatus.message, emoji: customStatus.emoji } : undefined}
        onSave={({ message, emoji }) => {
          updateStatus({ message, emoji });
          setShowStatusModal(false);
        }}
      />
    </>
  );
}
